export type OnboardingStartHandler = (initialSpotlightScreen?: string) => void;
export type OnboardingChangeHandler = (screen: string) => void;
export type OnboardingEndHandler = (screen: string) => void;
export type ShowSpotlightHandler = () => void;

export class WhiteboardOnboardingService {
	private startOnboardingCallback: OnboardingStartHandler;
	private onOnboardingChangeCallback: OnboardingChangeHandler;
	private onOnboardingEndCallback: OnboardingEndHandler;
	private handleShowTitleSpotlight: ShowSpotlightHandler;
	private handleShowHeaderSpotlight: ShowSpotlightHandler;
	private handleShowCollaboratorSpotlight: ShowSpotlightHandler;
	private _previousOnboardingScreen: string | undefined;
	private _currentOnboardingScreen: string | undefined;
	private _onboardingSkipped: boolean;
	private _hasStartedWhiteboardOnboardingSpotlight: boolean;
	private _isEligibleForGAOnboarding: boolean;
	private _isNewWhiteboard: boolean;

	constructor() {
		this.handleShowTitleSpotlight = () => {};
		this.startOnboardingCallback = () => {};
		this.onOnboardingChangeCallback = () => {};
		this.onOnboardingEndCallback = () => {};
		this.handleShowHeaderSpotlight = () => {};
		this.handleShowCollaboratorSpotlight = () => {};
		this._previousOnboardingScreen = undefined;
		this._currentOnboardingScreen = undefined;
		this._onboardingSkipped = false;
		this._hasStartedWhiteboardOnboardingSpotlight = false;
		this._isEligibleForGAOnboarding = false;
		this._isNewWhiteboard = false;
	}

	dispose() {
		this.handleShowTitleSpotlight = () => {};
		this.startOnboardingCallback = () => {};
		this.onOnboardingChangeCallback = () => {};
		this.onOnboardingEndCallback = () => {};
		this.handleShowHeaderSpotlight = () => {};
		this.handleShowCollaboratorSpotlight = () => {};
		this._previousOnboardingScreen = undefined;
		this._currentOnboardingScreen = undefined;
		this._onboardingSkipped = false;
		this._hasStartedWhiteboardOnboardingSpotlight = false;
		this._isEligibleForGAOnboarding = false;
		this._isNewWhiteboard = false;
	}

	/**
	 * This is called in Canvas to register the startOnboarding callback in
	 * Confluence, which will be used to let Canvas know when it should start its
	 * spotlight onboarding flow.
	 *
	 * @param callback A callback that indicates to Canvas to start it's onboarding
	 */
	registerStartOnboardingCallback(callback: OnboardingStartHandler) {
		this.startOnboardingCallback = callback;
	}

	/**
	 * This is called in Confluence to register the onOnboardingChange callback
	 * which will be called in Canvas via the Bridge API whenever a new onboarding
	 * screen is shown.
	 *
	 * @param callback A callback that is passed the new onboarding screen name
	 * whenever it changes
	 */
	registerOnOnboardingChangeCallback(callback: OnboardingChangeHandler) {
		this.onOnboardingChangeCallback = callback;
	}

	/**
	 * This is called in Confluence to register the onOnboardingEnd callback
	 * which will be called in Canvas once the onboarding flow is finished there.
	 * The callback that is registered will handle ending the onboarding tour.
	 *
	 * @param callback A callback that handles ending the onboarding tour
	 */
	registerOnOnboardingEndCallback(callback: OnboardingEndHandler) {
		this.onOnboardingEndCallback = callback;
	}

	/**
	 * This is called in Confluence when we have finished the section of
	 * the whiteboards spotlight onboarding that happens here. It lets Canvas know
	 * that we are ready to start the spotlight onboarding flow there.
	 */
	startOnboarding(initialSpotlightScreen?: string) {
		this.startOnboardingCallback(initialSpotlightScreen);
	}

	/**
	 * This is called in Canvas via the Bridge API whenever the onboarding screen
	 * changes
	 *
	 * @param {string} screen The new onboarding screen name
	 */
	onOnboardingChange(screen: string) {
		this.onOnboardingChangeCallback(screen);
	}

	/**
	 * This is called in Canvas once its onboarding is finished. It calls the
	 * function that's been registered to handle ending the onboarding tour.
	 *
	 * @param {string} screen The name of the onboarding screen that the flow ended on
	 */
	onOnboardingEnd(screen: string) {
		this.onOnboardingEndCallback(screen);
	}

	/**
	 * This is called by Confluence to register a callback function that will be invoked
	 * by `showTitleSpotlight` below whenever spotlight onboarding for the Whiteboard title
	 * should be shown. The callback is responsible for showing the spotlight.
	 */
	onShowTitleSpotlight(callback: ShowSpotlightHandler) {
		this.handleShowTitleSpotlight = callback;
	}

	/**
	 * This is called by Confluence to register a callback function that will be invoked
	 * by `showHeaderSpotlight` below whenever spotlight onboarding for the Content types
	 * header should be shown. The callback is responsible for showing the spotlight.
	 */
	onShowHeaderSpotlight(callback: ShowSpotlightHandler) {
		this.handleShowHeaderSpotlight = callback;
	}

	/**
	 * This is called by Confluence to register a callback function that will be invoked
	 * by `showCollaboratorSpotlight` below whenever spotlight onboarding for the Content types
	 * collaborators should be shown. The callback is responsible for showing the spotlight.
	 */
	onShowCollaboratorSpotlight(callback: ShowSpotlightHandler) {
		this.handleShowCollaboratorSpotlight = callback;
	}

	/**
	 * This invokes a callback handler that should show the onboarding spotlight
	 * targeting the Whiteboard title. This callback was registered elsewhere in
	 * Confluence by a previous call to `this.onShowTitleSpotlight(callback)`
	 */
	showTitleSpotlight() {
		this.handleShowTitleSpotlight();
	}

	/**
	 * This invokes a callback handler that should show the onboarding spotlight
	 * targeting the Content types header. This callback was registered elsewhere in
	 * Confluence by a previous call to `this.onShowHeaderSpotlight(callback)`
	 */
	showHeaderSpotlight() {
		this.handleShowHeaderSpotlight();
	}

	/**
	 * This invokes a callback handler that should show the onboarding spotlight
	 * targeting the Content types collaborators. This callback was registered elsewhere in
	 * Confluence by a previous call to `this.onShowHeaderSpotlight(callback)`
	 */
	showCollaboratorSpotlight() {
		this.handleShowCollaboratorSpotlight();
	}

	get previousOnboardingScreen(): string | undefined {
		return this._previousOnboardingScreen;
	}

	set previousOnboardingScreen(screen: string | undefined) {
		this._previousOnboardingScreen = screen;
	}

	get currentOnboardingScreen(): string | undefined {
		return this._currentOnboardingScreen;
	}

	set currentOnboardingScreen(screen: string | undefined) {
		this._currentOnboardingScreen = screen;
	}

	/**
	 * This is used to get the value of onboardingSkipped (Beta-only)
	 *
	 * @returns {boolean} value of onboardingSkipped
	 */
	get onboardingSkipped() {
		return this._onboardingSkipped;
	}

	/**
	 * This is called in Confluence to set the value of onboardingSkipped to true
	 * if the user exits the Beta onboarding modal early. If the user does this we do
	 * not want to show them the Beta onboarding spotlight.
	 */
	set onboardingSkipped(skipped: boolean) {
		this._onboardingSkipped = skipped;
	}

	get hasStartedWhiteboardOnboardingSpotlight() {
		return this._hasStartedWhiteboardOnboardingSpotlight;
	}

	set hasStartedWhiteboardOnboardingSpotlight(bool: boolean) {
		this._hasStartedWhiteboardOnboardingSpotlight = bool;
	}

	/**
	 * Indicates whether or not the current whiteboard being viewed already existed
	 * or was newly created. This is currently used to determine which onboarding
	 * flow to send a user through.
	 */
	get isNewWhiteboard() {
		return this._isNewWhiteboard;
	}

	set isNewWhiteboard(bool: boolean) {
		this._isNewWhiteboard = bool;
	}

	/**
	 * Indicates whether or not the current whiteboard being viewed already existed
	 * or was newly created. This is currently used to determine which onboarding
	 * flow to send a user through.
	 */
	get isEligibleForGAOnboarding() {
		return this._isEligibleForGAOnboarding;
	}

	set isEligibleForGAOnboarding(bool: boolean) {
		this._isEligibleForGAOnboarding = bool;
	}
}

export const onboardingService = new WhiteboardOnboardingService();
